import {Injectable} from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
  DocumentChangeAction
} from '@angular/fire/compat/firestore';
import {
  DownloadProgress,
  FileDocument,
  FileUpdateTimes,
  StorageFile,
  StorageMetadata,
  UploadProgress
} from './file.model';
import {BehaviorSubject, combineLatest, Observable, of, Subject} from 'rxjs';
import {AngularFireStorage, AngularFireStorageReference, AngularFireUploadTask} from '@angular/fire/compat/storage';
import {map, shareReplay, startWith, switchMap, tap} from 'rxjs/operators';
import {FileCache} from './filecache.service';
import {LoadRosterSyncReview} from '../../staged-analysis/interfaces/roster-sync-job-review.model';
import {HttpEvent} from '@angular/common/http';
import {AuditLogService} from '../../../core/services/audit-log/audit-log.service';
import {AuditLogProductCodes} from '../../../core/services/audit-log/auditLog.model';
import {AnalyticsService} from '../../../core/services/analytics/analytics.service';

import {environment} from '../../../../environments/environment';
import {AngularFireFunctions} from "@angular/fire/compat/functions";


@Injectable({
  providedIn: 'root'
})
export class FileService {

  fileDownloadProgress$: Observable<DownloadProgress>;
  fileUploadProgress$: Subject<UploadProgress> = new Subject<UploadProgress>();

  private filesCollectionRef_: AngularFirestoreCollection<AngularFirestoreDocument<FileDocument>> | undefined;
  private files$: Observable<DocumentChangeAction<AngularFirestoreDocument<FileDocument>>[]> | undefined;
  private customerId: string | undefined;
  private fileUpdateTimes$: BehaviorSubject<FileUpdateTimes> = new BehaviorSubject({});

  constructor(private afs: AngularFirestore,
              private storage: AngularFireStorage,
              private angularFireFunctions: AngularFireFunctions,
              private fileCache: FileCache,
              private auditLogService: AuditLogService,
              private analyticsService: AnalyticsService
              )
  {
    this.fileDownloadProgress$ = fileCache.fileDownloadProgress$.asObservable();
  }

  loadForCustomer(customerId: string): Observable<DocumentChangeAction<AngularFirestoreDocument<FileDocument>>[]> {
    this.customerId = customerId;
    this.fileCache.setCustomerId(customerId);
    this.filesCollectionRef_ = this.afs.collection('customers').doc(customerId).collection('fileUploads', ref => ref.orderBy('name'));
    this.files$ = this.filesCollectionRef_.snapshotChanges().pipe(shareReplay(1));
    return this.files$;
  }

  getFileDocById(customerId: string, fileId: string): Observable<FileDocument> {
    const fileDocRef = this.afs.collection('customers').doc(customerId).collection('fileUploads').doc<FileDocument>(fileId);
    return fileDocRef.valueChanges().pipe(shareReplay(1));
  }

  setCustomerId(customerId: string): void {
    this.customerId = customerId;
  }

  getFilesObservable(): Observable<DocumentChangeAction<AngularFirestoreDocument<FileDocument>>[]> {
    return this.files$;
  }

  uploadFileToBucket(file: File): AngularFireUploadTask  {
    return this.storage.ref(`${this.customerId}/${file.name}`).put(file, {
      customMetadata: {
        fromClient: 'true'
      },
      contentDisposition: `attachment;filename*=utf8''${file.name}`
    });
  }

  downloadAndParse(file: FileDocument): Observable<DownloadProgress> {
    return this.fileCache.getFile(file);
  }

  getDownloadUrl(file: FileDocument): Observable<string>  {
    const ref = this.storage.ref(`${this.customerId}/${file.name}`);
    return ref.updateMetadata({contentDisposition: `attachment;filename*=utf8''${file.name}`}).pipe(switchMap((result) => {
      return ref.getDownloadURL();
    }));
  }

  getDownloadUrlForLog(path: string) {
    const store = this.storage.storage.app.storage(environment.storage.logs);
    return store.ref(`${path}`).getDownloadURL();
  }

  parseLocalFile(file: File): Observable<DownloadProgress> {
    return this.fileCache.parseLocalFile(file);
  }

  validateFieldJoin({primaryFile, supportFile, joins}:
                      { primaryFile: FileDocument; supportFile: FileDocument; joins: { primaryField: string; childField: string }[] }) {
    const {primaryFields, supportFields} = joins.reduce(
      (acc, join) => {
        acc.primaryFields.push(join.primaryField);
        acc.supportFields.push(join.childField);
        return acc;
        },
      {primaryFields: [], supportFields: []});
    // This lookup will hit the cached copy of the data
    return combineLatest([
      this.downloadAndParse(primaryFile),
      this.downloadAndParse(supportFile)])
      .pipe(
        map(([primaryDownloadProgress, supportDownloadProgress]) => {
          if ((primaryDownloadProgress.complete && primaryDownloadProgress.success) &&
            (supportDownloadProgress.complete && supportDownloadProgress.success)) {
            const primaryFieldData = primaryDownloadProgress.data.map((data => this.createJoinIndex_(data, primaryFields)));
            const supportFieldData = supportDownloadProgress.data.map(data => this.createJoinIndex_(data, supportFields));

            const supportSet = new Set(supportFieldData); // A set of all support data keys in order to track matches from primary to support
            const primarySet = new Set(primaryFieldData); // A set of all primary data keys in order to track matches from support to primary

            // Row indices in primary that do not match support based on the given mappings
            const unmatchedPrimaryRecords = [];
            // Row indices in primary file that are duplicate records
            const duplicatesInPrimaryRecords = [];
            primaryDownloadProgress.data.forEach((row, index) => {
              const primaryKey = this.createJoinIndex_(row, primaryFields);
              if (!supportSet.has(primaryKey)) {
                unmatchedPrimaryRecords.push(index);
              }
              if (primarySet.has(primaryKey)) {
               primarySet.delete(primaryKey);
              } else {
                duplicatesInPrimaryRecords.push(index);
              }
            }, []);

            // Row indices in support file that do not match a record in primary data file
            const unmatchedSupportRecords = [];
            // Row indices in support file that are duplicate records
            const duplicatesInSupportRecords = [];

            // supportDownloadProgress.data.forEach((row, index) => {
            //   if (!primarySet.has(row[supportField])) {
            //     unmatchedSupportRecords.push(index);
            //   }
            //   if (supportSet.has(row[supportField])) {
            //     supportSet.delete(row[supportField]);
            //   } else {
            //     duplicatesInSupportRecords.push(index);
            //   }
            // });

            return {success: true, unmatchedSupportRecords, duplicatesInSupportRecords, unmatchedPrimaryRecords, duplicatesInPrimaryRecords};
          } else if ((primaryDownloadProgress.complete && !primaryDownloadProgress.success) ||
            (supportDownloadProgress.complete && !supportDownloadProgress.success)) {
            return {success: false, error: 'Error accessing job data files'};
          }
          return {message:'Still working'};
        })
      );
  }


  deleteFile(file: FileDocument): Observable<any> {
    return this.storage.ref(`${this.customerId}/${file.name}`).delete().pipe(
      tap(() => {
        this.auditLogService.addLogEntry({
          productCode: AuditLogProductCodes.LITTLE_SIS_PREMIUM,
          typeId: 22,
          actionId: 151,
          logData: { fileName: file.name}
        });

        this.analyticsService.sendEvent({
          action: 'delete_file',
          properties: {
            category: 'files_page',
            label: file.size.toString(),
          }
        });
      }),
      switchMap(
       resp => this.afs.collection('customers').doc(this.customerId).collection('fileUploads').doc(file.name).delete()
    ));
  }

  loadFileUpdateTimestamps(fileNames: string[], useCache = true): Observable<FileUpdateTimes> {
    if (!this.customerId) {
      throw new Error('Missing customer ID');
    }
    const callable = this.angularFireFunctions.httpsCallable('CloudStorage-getLastUpdatedTimes');
    callable({customerId: this.customerId, fileNames, useCache}).subscribe((result) => {
      if (this.fileUpdateTimes$.value) {
        this.fileUpdateTimes$.next(Object.assign(this.fileUpdateTimes$.value, result));
      } else {
        this.fileUpdateTimes$.next(result);
      }
    });
    return this.getFileUpdatesObservable();
  }

  getFileUpdatesObservable() {
    return this.fileUpdateTimes$.asObservable().pipe(startWith(this.fileUpdateTimes$.value));
  }

  downloadFile(bucketName: string, file: StorageFile): Observable<HttpEvent<LoadRosterSyncReview>> {
    const store = this.storage.storage.app.storage(bucketName);
    const fileRef = store.ref(`${file.name}`);
    const url = fileRef.getDownloadURL();
    return this.fileCache.getFileFromStorage(url);
  }

  async getMetadata(bucketName: string, file: StorageFile): Promise<StorageMetadata> {

    const store = this.storage.storage.app.storage(bucketName);

    const fileRef = store.ref(`${file.name}`);
    try {
      const metadata = await fileRef.getMetadata() as StorageMetadata;
      return metadata;
    } catch (e) {
      console.warn(e.message);
      return null;
    }
  }

  refreshFileData(file: FileDocument): Observable<DownloadProgress> {
    return this.fileCache.getFile(file, {refreshCache: true});
  }

  resetUpdatedTimestamp(selectedFile: FileDocument) {
    return this.loadFileUpdateTimestamps([selectedFile.name], false);
  }

  private createJoinIndex_(rowData: any[], fields: string[]): string {
    return fields.map((f) => rowData[f]).join('|');
  }
}
